import type { IconName } from "@keydials/dial-workspace";

/**
 * The app themes (KEY-755) — the ONE list every theme picker, the stored-value check and the type read from.
 *
 * Adding a theme is data, in three places that must agree on its id:
 *  1. an entry here;
 *  2. a `[data-theme='<id>']` block in `theme.css` with the full token set (copy Professional's — every token,
 *     including the dial's `--dial-*` ones), mirrored under `.kd-workspace-scope` in
 *     `packages/dial-workspace/src/styles.css` for the admin preview;
 *  3. the id in the API's allow-list, `KeyDials.Domain/Instances/AppThemes.cs` — without it the pick is refused
 *     and never follows the person to another device.
 */
export interface ThemeDefinition {
  /** The `data-theme` value, the stored value and the API's id. Lower-case, short, never renamed once shipped. */
  id: string;
  label: string;
  description: string;
  /** Whether it is a dark ground — what an OS dark-mode preference picks on a first visit. */
  dark: boolean;
  icon: IconName;
  /** Still in design: offered only where the server lists it (`Features:SoftTheme`, dev not prod). */
  preview?: boolean;
}

export const THEMES = [
  {
    id: "professional",
    label: "Professional",
    description: "Crisp and light — the standard Key Dials look.",
    dark: false,
    icon: "sun",
  },
  {
    id: "dark",
    label: "Dark",
    description: "Easy on the eyes in a dim room.",
    dark: true,
    icon: "moon",
  },
  {
    id: "soft",
    label: "Soft",
    description: "Soft — a calm, light look (placeholder until the designer's palette lands)",
    dark: false,
    icon: "palette",
    preview: true,
  },
] as const satisfies readonly ThemeDefinition[];

export type ThemeName = (typeof THEMES)[number]["id"];

/** What somebody gets with no stored choice and no OS dark preference. */
export const DEFAULT_THEME: ThemeName = "professional";

/** The known id matching a stored or server value, or null — so a typo, an old build's value or a theme
 * retired since it was picked falls back to the default instead of drawing an unstyled page. */
export function parseThemeName(value: unknown): ThemeName | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return THEMES.find((t) => t.id === trimmed)?.id ?? null;
}

export function themeDefinition(name: ThemeName): (typeof THEMES)[number] {
  return THEMES.find((t) => t.id === name) ?? THEMES[0];
}

/**
 * The themes a picker may list. `available` is the server's list from `GET /api/me/theme`; before it has
 * answered (the sign-in page, a guest, a locked screen) only the finished themes are offered, so a preview
 * theme never shows anywhere the server hasn't said it may.
 */
export function offeredThemes(available: readonly string[] | null): (typeof THEMES)[number][] {
  return THEMES.filter((t) => (available ? available.includes(t.id) : !("preview" in t && t.preview)));
}

/** The first-visit theme: the first dark theme when the OS prefers dark, otherwise the default. */
export function defaultTheme(prefersDark: boolean): ThemeName {
  if (prefersDark) {
    const dark = THEMES.find((t) => t.dark);
    if (dark) return dark.id;
  }
  return DEFAULT_THEME;
}
